import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// WAVE 1, 2026-10-28 -- humanized rewrite of the Phase-3 sweep evidence doc.
// Isolated, unlinked, noindexed preview. Numbers re-verified fresh against
// data/feature_search_phase3.json, data/residual_noisy_copy_control.json,
// and data/seasonality_subperiod_check.json before writing.
//
// CHARTLESS, deliberately: this piece's tension is comparative and
// conceptual across 6 candidates that fail in three different ways
// (restating an existing feature / adding nothing once controlled for /
// real-but-too-small), not a single clean visual pattern -- the honest
// default per the locked template rule, and the one piece in this wave
// where a chart would have been decorative rather than load-bearing.

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=phase3-sweep";

export function Phase3SweepArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "Six ideas, each one statistically real, none of them useful — Tickerpicker",
    description:
      "A pre-registered sweep of eight price/volume candidates. Several clear significance by enormous margins. " +
      "None of them make it into the model, and the reasons are more interesting than a flat no.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Six ideas, each one statistically real, none of them useful",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
            <a href="/">Home</a>
            <span className="mm-crumb-sep">/</span>
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — price/volume sweep</div>
        <h1>Six ideas, each one statistically real, none of them useful</h1>

        <p>
          We pre-registered a sweep of eight price-and-volume candidates — idiosyncratic volatility, market
          beta, a liquidity-cost measure, calendar seasonality, and a few others — and tested each one properly
          before looking at a single result. Two of the eight (turnover spikes and the 52-week-high effect) have
          their own write-ups elsewhere on this page. This is about the other six: none of them shipped, but not
          because they're weak.
        </p>

        <p>
          Two of the six post some of the strongest raw numbers we've measured anywhere in this project.
          Idiosyncratic volatility clears significance at a t-statistic past 12. The largest single-day move in
          the past month clears it past 11. These aren't borderline, arguable results. They're overwhelming.
        </p>

        <blockquote className="hatpl-pullquote">
          A feature can be overwhelmingly significant and still add nothing, because it only restates what the
          model already has.
        </blockquote>

        <p>
          Both fail the same way: once you remove what our existing features already explain, there's nothing
          left. Idiosyncratic volatility turns out to be 71% correlated with the realized-volatility feature
          already sitting in the model — different name, same information. The single-day-move feature is even
          more redundant (80% correlated with the same thing), and worse, whatever's genuinely left over after
          accounting for that overlap points the <em>opposite</em> direction from its raw signal — a sign flip
          that disqualifies it outright. Two more candidates, market beta and a liquidity-cost measure, fail on
          related but gentler ground: their raw correlation is real, but it's far more modest, and nothing
          survives once existing information is removed.
        </p>

        <p>
          A fifth, six-month momentum, is the simplest kind of failure — no real correlation at all, and its
          sign doesn't even agree between the first and second half of the test.
        </p>

        <p>
          The sixth is the interesting one. Calendar seasonality clears every gate in this sweep on its own
          terms — significant, consistent in sign, uncorrelated with existing features, and genuinely
          incremental. Taken at face value, it should have shipped. It didn't, because a separate, more careful
          check of our own testing process found something uncomfortable: our incremental-value test has a
          built-in blind spot. Feed it a synthetic copy of an already-known-good feature — something that adds
          nothing by construction — and at moderate correlation levels, it still reports a small, statistically
          significant "incremental" signal. That's not seasonality's fault; it's a measured floor in our own
          instrument. Correct seasonality's result for that floor and the remainder is real, positive, and too
          small to be worth deploying on its own. There's a second problem on top of that: the effect isn't
          stable over time either. Broken out by year, 2020 runs negative, and one of five rolling-window folds
          does too — the strongly positive pooled average leans hard on the single most recent year, 2026, which
          reads far above every earlier period. Two separate reasons to hold off, not one. A different failure
          than the other five either way — not wrong, just not enough.
        </p>

        <p>
          "Significant" was never going to be the bar here. Knowing something is a real pattern in the data is a
          different question from knowing it tells the model something it doesn't already know, and this sweep
          is six separate reminders of that gap.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <table>
            <tbody>
              <tr><td>idio_vol — raw IC / t / FDR p</td><td>&minus;0.0647 / &minus;12.64 / 1.3&times;10&#8315;&sup3;&#8304;</td></tr>
              <tr><td>idio_vol — fails on</td><td>decorrelation (&rho;=0.71 vs. stored volatility)</td></tr>
              <tr><td>max_1m — raw IC / t / FDR p</td><td>&minus;0.0515 / &minus;11.58 / 1.1&times;10&#8315;&sup2;&#8310;</td></tr>
              <tr><td>max_1m — fails on</td><td>decorrelation (&rho;=0.80) + incremental sign flip (+0.0074)</td></tr>
              <tr><td>amihud_21 — raw IC / t / FDR p</td><td>&minus;0.0426 / &minus;7.46 / 1.2&times;10&#8315;&sup1;&sup2;</td></tr>
              <tr><td>amihud_21 — fails on</td><td>incremental value (residual FDR p=0.455)</td></tr>
              <tr><td>beta_252 — raw IC / t / FDR p</td><td>&minus;0.0297 / &minus;3.61 / 4.6&times;10&#8315;&#8308;</td></tr>
              <tr><td>beta_252 — fails on</td><td>incremental value (residual FDR p=0.194)</td></tr>
              <tr><td>mom_6m — raw IC / t / FDR p</td><td>+0.0002 / 0.04 / 0.966 (not significant)</td></tr>
              <tr><td>mom_6m — fails on</td><td>no real correlation, sign disagrees discovery vs. confirm</td></tr>
              <tr><td>season_month — raw IC / t / FDR p</td><td>+0.0137 / 3.72 / 3.7&times;10&#8315;&#8308;</td></tr>
              <tr><td>season_month — clears all 5 sweep gates, but:</td><td>&nbsp;</td></tr>
              <tr><td>&nbsp;&nbsp;measured false-positive floor (&rho; 0.70&ndash;0.90)</td><td>+0.0031 to +0.0033 (itself FDR-significant)</td></tr>
              <tr><td>&nbsp;&nbsp;season_month's own residual IC</td><td>+0.0071 (t=3.47, two independent re-runs agree)</td></tr>
              <tr><td>&nbsp;&nbsp;floor-corrected effect</td><td>&asymp;+0.0039 — real, too small to deploy</td></tr>
              <tr><td>&nbsp;&nbsp;stability check (by year / rolling fold)</td><td>not uniform — 2020 and one fold run negative</td></tr>
              <tr><td>Sweep method</td><td>pre-registered, 445 trading dates, BH-FDR across the 8-candidate family, 200+200 placebo calibration</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Sources: <code>backend/feature_search_phase3.py</code> (read-only sweep) &rarr;
            <code>data/feature_search_phase3.json</code>; <code>backend/residual_noisy_copy_control.py</code>
            &rarr; <code>data/residual_noisy_copy_control.json</code>;
            <code>backend/seasonality_subperiod_check.py</code> &rarr;
            <code>data/seasonality_subperiod_check.json</code>. All three read-only.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
